import { useIdle } from '@corets/use-idle';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@service-victoria/component-library/components/Button';
import Error from '@service-victoria/component-library/components/Notification/Error';
import Body from '@service-victoria/component-library/components/Text/Body';
import Heading from '@service-victoria/component-library/components/Text/Heading';
import Link from '@service-victoria/component-library/components/Text/Link';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import InputField from 'Components/InputField';
import {
  CallToActionContainer,
  ContentContainer,
  Form
} from 'Components/KioskRegistration/KioskRegistration';
import Page from 'Components/Page';
import { useDeviceRegistration, useRegisteredLocations } from 'hooks';
import { constants } from 'utils';
import { trackEvent } from 'utils/analytics';
import { Routes } from 'utils/constants';

import { schema } from './schema';

const KIOSK_CODE_LENGTH = 8;

interface FormValues {
  kioskCode: string;
}

const Register: React.FC = () => {
  const location = useLocation<
    { isForbiddenError?: boolean; transactionId?: string } | undefined
  >();
  const [transactionId] = useState(location.state?.transactionId ?? uuid());
  const history = useHistory();

  const { selectedLocation } = useRegisteredLocations();

  const isIdle = useIdle({
    // check every 10s
    interval: 10 * 1000,
    // user as idle after 1min
    threshold: 60 * 1000,
    // events that should reset the idle status (default: ["mousemove", "mouseup", "keyup", "focus", "resize"])
    events: ['mouseup', 'touchend', 'keyup', 'focus']
  });

  const {
    registerDevice: { mutate, isLoading, error }
  } = useDeviceRegistration({
    onRegisterDeviceSuccess: () => {
      trackEvent({
        label: 'submitDeviceRegistration',
        action: 'KioskAdmin'
      });
      history.push(constants.Routes.Verify, {
        transactionId
      });
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(schema)
  });

  const navigateToCheckInPage = () => {
    trackEvent({
      action: 'KioskAdmin',
      label: 'returnToCheckIn'
    });
    history.push(Routes.CheckIn);
  };

  const onSubmit: SubmitHandler<FormValues> = async data => {
    mutate({
      kioskCode: data.kioskCode
    });
  };

  useEffect(() => {
    if ([404, 400].includes(error?.response?.status ?? 0)) {
      setError('kioskCode', {
        type: 'manual',
        message: constants.GenericValidationErrors.invalidSecurityOrKioskCode
      });
    }
  }, [error?.response?.status, setError]);

  useEffect(() => {
    if (isIdle && selectedLocation) {
      navigateToCheckInPage();
      trackEvent({
        label: 'redirectInactiveToCheckin',
        action: 'KioskCheckIn',
        data: {
          locationId: selectedLocation?.locationId ?? ''
        }
      });
    }
    // eslint-disable-next-line
  }, [isIdle]);

  const navigateToRegisteredLocationPage = () => history.push(constants.Routes.RegisteredLocation);
  return (
    <Page
      banner={{
        title: selectedLocation ? selectedLocation.businessName : constants.Page.registrationTitle,
        size: 'small'
      }}>
      <Heading.H2 fontWeight={600} data-testid='PageHeading'>
        Enter kiosk code
      </Heading.H2>
      <ContentContainer>
        <Body data-testid='PageBody'>
          Enter the kiosk code that appears in the email you received or from the Victorian
          Government QR Code Service portal.
        </Body>
        <Link
          href='https://www.coronavirus.vic.gov.au/register-to-use-vic-gov-qr-code-service'
          target='_blank'
          data-testid='HelpLink'>
          I need help finding the code.
        </Link>
      </ContentContainer>

      <Form onSubmit={handleSubmit(onSubmit)} action='#' data-testid='Form'>
        <InputField
          type='tel'
          {...register('kioskCode')}
          disabled={isLoading}
          label={`${KIOSK_CODE_LENGTH} digit kiosk code`}
          maxLength={KIOSK_CODE_LENGTH}
          error={errors.kioskCode?.message}
        />
        {(error?.response?.status ?? 0) >= 500 && !isSubmitting && (
          <div data-testid='NetworkError'>
            <Error.Text title='Oops! Something went wrong!' value='Please try again shortly' />
          </div>
        )}
        {location.state?.isForbiddenError && !isSubmitting && (
          <div data-testid='ForbiddenError'>
            <Error.Text
              title='Oops! Something went wrong!'
              value='Please register your device with location again'
            />
          </div>
        )}
        <CallToActionContainer>
          <Button
            type='submit'
            disabled={isLoading}
            isLoading={isLoading}
            data-testid='RegisterSubmitButton'>
            Next
          </Button>
          {selectedLocation && (
            <Button
              buttonType='secondary'
              onClick={navigateToRegisteredLocationPage}
              disabled={isLoading}
              data-testid='RegisterBackButton'>
              Back
            </Button>
          )}
        </CallToActionContainer>
      </Form>
    </Page>
  );
};

export default Register;
